<script setup lang="ts">
import { ref, computed } from 'vue';
import AuthLayout from '../components/AuthLayout.vue';
import { Button, Input } from '../components/ui';
import { resetPassword } from '../api';
import { signInPath } from '../services/authPaths';
import { isValidEmail } from '../services/validators';

const email = ref('');
const emailError = ref('');
const loading = ref(false);
const success = ref(false);

const topLinks = computed(() => {
  if (success.value) {
    return [{ url: signInPath(email.value), text: 'Back to sign in' }];
  }
  return [
    { url: signInPath(), text: 'Sign in' },
    { url: '/users/confirmation/new', text: "Didn't receive confirmation instructions?" },
    { url: '/users/unlock/new', text: "Didn't receive unlock instructions?" },
  ];
});

const bottomLinks = computed(() => {
  if (success.value) return [];
  return [{ url: '/users/request', text: 'Not a member yet? Request access' }];
});

async function handleSubmit() {
  emailError.value = '';

  if (!isValidEmail(email.value)) {
    emailError.value = 'Enter a valid email.';
    return;
  }

  loading.value = true;

  try {
    await resetPassword(email.value);
    success.value = true;
  } catch {
    emailError.value = 'Something went wrong. Please try again.';
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AuthLayout title="Reset your password" :top-links="topLinks" :bottom-links="bottomLinks">
    <div v-if="success" class="text-white/80 text-base leading-relaxed">
      We've sent a reset link to your provided email with further instructions.
    </div>

    <form v-else @submit.prevent="handleSubmit">
      <Input v-model="email" type="email" placeholder="Email" autocomplete="email" :error="emailError" />

      <div class="mt-form-gap">
        <Button type="submit" variant="primary" size="lg" :loading="loading" :disabled="loading">
          Reset my password
        </Button>
      </div>
    </form>
  </AuthLayout>
</template>
